<template>
    <bottom-sheet v-model="shows" title="프로그램 참여 신청" fullscreen persistent width="1080" maxWidth="95%" needsLogin>
        <template #activator="{ attrs, on }">
            <slot name="activator" v-bind="{ attrs, on, loading }" />
        </template>
        <v-layout column justify-center class="bottom-sheet__contents h-100">
            <template v-if="loading">
                <v-overlay absolute color="white">
                    <v-progress-circular indeterminate size="100" width="5" color="primary" />
                </v-overlay>
            </template>
            <template v-else>
                <template v-if="step == STEPS.FINISH.value">
                    <v-sheet class="d-flex flex-column align-center">
                        <i class="icon icon-check mb-20 mb-lg-40"></i>
                        <v-sheet class="text-center mb-20 mb-lg-40">
                            <h3 class="tit mb-8 mb-lg-16">참여신청 완료</h3>
                            <p class="page-text--lg grey-6--text">"{{ program.subject }}" 프로그램에 대한 신청이 완료되었습니다.</p>
                        </v-sheet>
                        <div class="v-btn-group mt-0">
                            <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="exit"><span>확인</span></v-btn>
                        </div>
                    </v-sheet>
                </template>
                <template v-else>
                    <div class="text-center mb-20 mb-lg-40">
                        <h3 class="tit">프로그램 참여 신청</h3>
                    </div>
                    <v-sheet class="scroll-primary pr-10" style="flex: 0 1 auto; overflow-y: auto; overflow-x: hidden">
                        <v-stepper v-model="step">
                            <v-stepper-items>
                                <v-stepper-content :step="STEPS.COMMON.value" class="pa-0">
                                    <form-common v-model="form" v-on="{ setIsTermsValid }" @input="emit" />
                                </v-stepper-content>
                                <v-stepper-content :step="STEPS.RESUME.value" class="pa-0">
                                    <form-resume v-model="form" v-bind="{ hashTags }" @input="emit" />
                                </v-stepper-content>
                            </v-stepper-items>
                        </v-stepper>
                    </v-sheet>
                    <div class="v-btn-group">
                        <template v-if="step == STEPS.COMMON.value">
                            <v-btn outlined color="grey-d" class="min-w-140px min-w-lg-160px" @click="exit">
                                <span class="grey-6--text"> 취소하기 </span>
                            </v-btn>
                            <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="next">
                                <span> 다음으로 </span>
                            </v-btn>
                        </template>
                        <template v-if="step == STEPS.RESUME.value">
                            <v-btn outlined color="grey-d" class="min-w-140px min-w-lg-160px" @click="prev">
                                <span class="grey-6--text"> 이전으로 </span>
                            </v-btn>
                            <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="save">
                                <span> 신청하기 </span>
                            </v-btn>
                        </template>
                    </div>
                </template>
            </template>
        </v-layout>
    </bottom-sheet>
</template>

<script>
import api from "@/api";
import { mapGetters } from "vuex";
import { USER_FILE_TYPES } from "@/assets/variables";
import { initExpoEmployee, initExpoProgram } from "@/assets/variables/inits";

import BottomSheet from "@/components/dumb/bottom-sheet.vue";
import FormCommon from "./form/form-common.vue";
import FormResume from "./form/form-resume.vue";

const STEPS = {
    COMMON: { value: "COMMON", text: "기본정보" },
    RESUME: { value: "RESUME", text: "개인정보" },
    FINISH: { value: "FINISH", text: "신청완료" },
};

const steps = Object.values(STEPS).map((item, index) => ({ ...item, step: index + 1 }));

export default {
    components: {
        BottomSheet,
        FormCommon,
        FormResume,
    },
    props: {
        program: { type: Object, default: initExpoProgram },
    },
    data: () => ({
        form: initExpoEmployee(),
        hashTags: [],
        isTermsValid: undefined,

        shows: false,
        loading: false,

        step: steps[0].value,
        steps,
        STEPS,
    }),
    computed: {
        ...mapGetters(["_user"]),
        _program() {
            return this.program._id;
        },
        isCreate() {
            return !this.form?._id;
        },
    },
    methods: {
        async init() {
            if (this.loading) return;
            else this.loading = true;
            try {
                const { _program } = this;
                const { employees } = await api.v1.person.expo.employees.gets({
                    params: { _program },
                });
                let employee = employees?.[0];
                if (!employee) {
                    const { resume } = await api.v1.person.resume.get();
                    employee = {
                        info: {
                            name: resume?.personalInformation?.name,
                            phone: resume?.personalInformation?.phone?.phoneNumberFormat?.(),
                            email: resume?.personalInformation?.email,
                        },
                        resume,
                    };
                }
                this.form = initExpoEmployee(employee);
                this.hashTags = (await api.v1.setting.get())?.setting?.hashTags || [];

                if (this.form._id) {
                    this.$emit("input", this.form);
                }
            } finally {
                this.loading = false;
            }
        },
        async exit() {
            if (!this.form._id) {
                let keys_info = Object.keys(this.form.info);
                if (keys_info.some((key) => !!this.form.info[key])) {
                    const go = confirm("작성중인 내용이 있습니다.\n취소 시 작성중인 내용이 사라집니다.");
                    if (!go) return;
                }
            }
            this.shows = false;

            if (this.form._id) {
                this.$emit("input", this.form);
            }
        },
        async next() {
            if (!this.validate()) return;

            let index = this.steps.findIndex(({ value }) => value == this.step);
            this.step = this.steps[index + 1]?.value;
        },
        async prev() {
            let index = this.steps.findIndex(({ value }) => value == this.step);
            this.step = this.steps[index - 1]?.value;
        },
        async save() {
            if (!this.validate()) return;

            /** @type {import("@/plugins/vue-validate").Criterion[]} */
            let criteria = [
                {
                    key: "personalInformation.name",
                    message: "이름을 입력하세요",
                },
                {
                    key: "personalInformation.bornAt",
                    message: "생년월일을 입력하세요",
                },
                {
                    key: "personalInformation.gender",
                    message: "성별을 입력하세요",
                },
                {
                    key: "personalInformation.phone",
                    message: "연락처를 입력하세요",
                },
                {
                    key: "personalInformation.email",
                    message: "이메일을 입력하세요",
                },
                {
                    key: "personalInformation.residence.postcode",
                    message: "우편번호를 입력하세요",
                },
                {
                    key: "personalInformation.residence.address1",
                    message: "기본주소를 입력하세요",
                },
                {
                    key: "personalInformation.residence.address2",
                    message: "상세주소를 입력하세요",
                },
                {
                    key: "name",
                    message: "이력서 제목을 입력하세요",
                },
            ];

            let thisValidates = this.$validate(this.form.resume, criteria);
            if (!thisValidates) return;

            if (this.loading) return;
            else this.loading = true;
            try {
                let {
                    resume: { profile, ...resume },
                    ...form
                } = this.form;

                form._program = this._program;

                if (profile instanceof File) {
                    profile = (await api.v1.me.files.post({ type: USER_FILE_TYPES.PROFILE.value }, profile))?.file;
                }
                resume._profile = profile?._id;
                form.resume = resume;

                let { post, put } = api.v1.person.expo.employees;
                let { employee } = await (this.isCreate ? post : put)(form);
                this.form = initExpoEmployee(employee);
                this.next();
            } finally {
                if (this.loading) this.loading = false;
            }
        },

        emit() {
            this.form = initExpoEmployee(this.form);
        },
        setIsTermsValid(isTermsValid) {
            this.isTermsValid = isTermsValid;
        },
        validate() {
            try {
                switch (this.step) {
                    case STEPS.COMMON.value: {
                        if (!this.form.info.desc) throw new Error("구직회원 소개를 입력해주세요");
                        if (!this.form.info.name) throw new Error("이름을 입력해주세요");
                        if (!this.form.info.phone) throw new Error("연락처를 입력해주세요");
                        if (!this.form.info.email) throw new Error("이메일을 입력해주세요");
                        if (!this.isTermsValid) throw new Error("필수약관에 동의해주세요");
                        break;
                    }
                }

                return true;
            } catch (error) {
                alert(error.message);
                return false;
            }
        },
    },
    watch: {
        _program() {
            this.init();
        },
        _user() {
            this.init();
        },
        shows(shows) {
            if (shows) this.$nextTick(this.init);
        },
    },
    mounted() {
        this.init();
    },
};
</script>
